import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Car, 
  Globe, 
  MessageSquare, 
  LogOut, 
  Menu, 
  X,
  Mail,
  Settings,
  TrendingUp,
  Shield,
  Users,
  Image
} from 'lucide-react';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [admin, setAdmin] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('admin');
    
    if (!token) {
      navigate('/admin/login');
    } else {
      setAdmin(JSON.parse(adminData));
    }
  }, [navigate]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('admin');
      navigate('/admin/login');
    }
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
    { path: '/admin/cars', icon: Car, label: 'Manage Cars' },
    { path: '/admin/tours', icon: Globe, label: 'Manage Tours' },
    { path: '/admin/team', icon: Users, label: 'Team Members' },
    { path: '/admin/hero-slides', icon: Image, label: 'Hero Slides' },
    { path: '/admin/inquiries', icon: MessageSquare, label: 'Inquiries' },
    { path: '/admin/contacts', icon: Mail, label: 'Contact Messages' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-dark-800">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 bg-dark-900 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out w-64 shadow-xl`}>
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="p-4 border-b border-dark-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="bg-orange-500 p-2 rounded-lg">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg">HYPA MOTORS</h2>
                  <p className="text-orange-500 text-xs">Admin Panel</p>
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-gray-400 md:hidden hover:text-white">
                <X size={20} />
              </button>
            </div>
            {admin && (
              <div className="mt-3 pt-3 border-t border-dark-700">
                <p className="text-gray-500 text-xs">Logged in as</p>
                <p className="text-white text-sm font-semibold">{admin.username}</p>
                <p className="text-gray-500 text-xs">{admin.email}</p>
              </div>
            )}
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-400 hover:bg-dark-800 hover:text-white'
                }`}
              >
                <item.icon size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Footer Area */}
          <div className="p-4 border-t border-dark-700">
            <button
              onClick={handleLogout}
              className="flex items-center justify-center space-x-2 w-full px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all duration-200"
            >
              <LogOut size={16} />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${sidebarOpen ? 'md:ml-64' : ''} transition-margin duration-300`}>
        {/* Top Bar */}
        <div className="bg-dark-900 shadow-md sticky top-0 z-40 border-b border-dark-700">
          <div className="px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-dark-800 transition-colors text-gray-400 hover:text-white"
            >
              <Menu size={20} />
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{admin?.username || 'Admin'}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {admin?.username?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-6 bg-dark-800 min-h-screen">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;